import { Menu as AntMenu } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiMenu.less";
import type {
  IBuiMenuDividerProps,
  IBuiMenuItemProps,
  IBuiMenuProps,
  IBuiSubMenuProps,
} from "./BuiMenu.types";

/**
 * BuiMenuItem sub-component for individual menu items.
 * A styled wrapper around antd Menu.Item with full access to its API.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the menu item.
 * @param root0.className - Additional CSS class names.
 */
const BuiMenuItem = ({ testid, className, ...props }: IBuiMenuItemProps) => (
  <AntMenu.Item
    data-testid={testid}
    className={cn("bui-menu-item", className)}
    {...props}
  />
);

/**
 * BuiSubMenu sub-component for individual sub-menu items.
 * A styled wrapper around antd Menu.SubMenu with full access to its API.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the sub-menu item.
 * @param root0.className - Additional CSS class names.
 * @param root0.popupClassName - Additional CSS class names for the popup submenu.
 * @example
 * ```tsx
 *<BuiMenu testid="nav-menu">
 *  <BuiMenu.Item testid="plans">
 *    <a href="/plans">Plans</a>
 *  </BuiMenu.Item>
 *  <BuiSubMenu testid="resources" title="Resources">
 *    <BuiMenu.Item testid="docs">
 *      <a href="/docs">Documents</a>
 *    </BuiMenu.Item>
 *  </BuiSubMenu>
 * </BuiMenu>
 * ```
 */
const BuiSubMenu = ({
  testid,
  className,
  popupClassName,
  ...props
}: IBuiSubMenuProps) => (
  <AntMenu.SubMenu
    {...props}
    data-testid={testid}
    className={cn("bui-submenu", className)}
    popupClassName={cn("bui-submenu-popup", popupClassName)}
  />
);

/**
 * BuiMenuDivider sub-component for visual separation between menu items.
 * A styled wrapper around antd Menu.Divider with full access to its API.
 * @param root0 - The component props.
 * @param root0.className - Additional CSS class names.
 */
const BuiMenuDivider = ({ className, ...props }: IBuiMenuDividerProps) => (
  <AntMenu.Divider className={cn("bui-menu-divider", className)} {...props} />
);

/**
 * BuiMenu component - a styled wrapper around antd Menu.
 * Provides full access to antd's Menu API with Blueprint styling.
 * Intended for use as dropdown content with antd Dropdown or BTDropdown.
 * @param root0 - The component props.
 * @param root0.testid - Test ID for the menu.
 * @param root0.className - Additional CSS class names.
 * @example
 * ```tsx
 * import { Dropdown } from "antd";
 *
 * <Dropdown
 *   overlay={
 *     <BuiMenu testid="nav-menu">
 *       <BuiMenu.Item testid="plans">
 *         <a href="/plans">Plans</a>
 *       </BuiMenu.Item>
 *       <BuiMenu.Item testid="docs">
 *         <a href="/docs">Documents</a>
 *       </BuiMenu.Item>
 *     </BuiMenu>
 *   }
 *   trigger={["click"]}
 * >
 *   <button aria-haspopup="true">...</button>
 * </Dropdown>
 * ```
 */
const BuiMenuComponent = ({ testid, className, ...props }: IBuiMenuProps) => (
  <AntMenu
    data-testid={testid}
    className={cn("bui-menu", className)}
    {...props}
  />
);

/**
 * BuiMenu component - a styled wrapper around antd Menu.
 * Provides full access to antd's Menu API with Blueprint styling.
 * Intended for use as dropdown content with antd Dropdown or BTDropdown.
 * @example
 * ```tsx
 * import { Dropdown } from "antd";
 *
 * <Dropdown
 *   overlay={
 *     <BuiMenu testid="nav-menu">
 *       <BuiMenu.Item testid="plans">
 *         <a href="/plans">Plans</a>
 *       </BuiMenu.Item>
 *       <BuiMenu.Item testid="docs">
 *         <a href="/docs">Documents</a>
 *       </BuiMenu.Item>
 *     </BuiMenu>
 *   }
 *   trigger={["click"]}
 * >
 *   <button aria-haspopup="true">...</button>
 * </Dropdown>
 * ```
 */
export const BuiMenu = Object.assign(BuiMenuComponent, {
  /**
   * BuiMenuItem sub-component for individual menu items.
   * A styled wrapper around antd Menu.Item with full access to its API.
   */
  Item: BuiMenuItem,
  /**
   * BuiSubMenu sub-component for individual sub-menu items.
   * A styled wrapper around antd Menu.SubMenu with full access to its API.
   * @example
   * ```tsx
   *<BuiMenu testid="nav-menu">
   *  <BuiMenu.Item testid="plans">
   *    <a href="/plans">Plans</a>
   *  </BuiMenu.Item>
   *  <BuiSubMenu testid="resources" title="Resources">
   *    <BuiMenu.Item testid="docs">
   *      <a href="/docs">Documents</a>
   *    </BuiMenu.Item>
   *  </BuiSubMenu>
   * </BuiMenu>
   * ```
   */
  SubMenu: BuiSubMenu,
  /**
   * BuiMenuDivider sub-component for visual separation between menu items.
   * A styled wrapper around antd Menu.Divider with full access to its API.
   */
  Divider: BuiMenuDivider,
});
